import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { USER_ACTIVITY_INFO, type UserActivityActionCount } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDateTime } from './adminFormat'

interface ActivityFailuresDialogProps {
  /** The function whose failures are being read. Mounted only while open. */
  row: UserActivityActionCount
  onClose: () => void
}

/**
 * Where one function failed, and who it failed for.
 *
 * A dialog rather than another column, the same bargain as the People list:
 * the cell stays a count, and the meetings only appear when somebody asks.
 * Newest first, which is the order the report already returns them in.
 */
export default function ActivityFailuresDialog({ row, onClose }: ActivityFailuresDialogProps) {
  const { t, locale } = useUiStrings()
  const closeRef = useRef<HTMLButtonElement>(null)
  const label = USER_ACTIVITY_INFO[row.action].label

  useEffect(() => {
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-activity-failures-title"
      data-testid="activity-failures-dialog"
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="relative flex max-h-[70vh] w-full max-w-md flex-col rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl">
        <header className="flex shrink-0 items-start gap-3 border-b border-[var(--border-color)] px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2
              id="admin-activity-failures-title"
              className="text-base font-bold text-[var(--text-primary)]"
            >
              {t.adminActivityFailuresTitle}
            </h2>
            <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">{label}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            {t.close}
          </button>
        </header>

        <ul className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {row.failures.map((failure) => (
            <li
              key={failure.id}
              className="border-b border-[var(--border-color)] py-2.5 last:border-0"
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="min-w-0 truncate text-sm font-medium text-[var(--text-primary)]">
                  {failure.name}
                </p>
                <p className="shrink-0 text-xs tabular-nums text-[var(--text-tertiary)]">
                  {formatDateTime(failure.createdAt, locale)}
                </p>
              </div>
              {failure.email ? (
                <p className="truncate text-xs text-[var(--text-secondary)]">{failure.email}</p>
              ) : null}
              <p className="mt-0.5 truncate text-sm text-[var(--text-primary)]">
                {failure.meetingTitle ?? t.adminActivityNoMeeting}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  )
}
